import { Button, Popover } from "@shopify/polaris"
import React, { useContext, useEffect, useRef, useState } from "react"
import CountUp from "react-countup"
import { useGetCreditsAvailable } from "../../hooks/useAI.js"
import { GlowingButtonContainer } from "../GlowingButtonContainer.jsx"
import { AIStateContext } from "../providers/AIStateProvider.jsx"
import { GlobalStateContext } from "../providers/GlobalStateProvider.jsx"
import { CoolButton } from "../CoolButton.jsx"
import { useTranslation } from "react-i18next"

const CREDIT_BUNDLE_IDS_DEV = {
	pri_01hfrkvvn8a9hbpr031y594knn: {
		costInUsd: 1,
		credits: "1K",
		extra: null,
	},
	pri_01hksn6zgp95rqs0vyd83pw75n: {
		costInUsd: 5,
		credits: "5K",
		extra: null,
	},
	pri_01hksn8fqkd5bbtajgeshcfpjp: {
		costInUsd: 10,
		credits: "10K",
		extra: "1K",
	},
	pri_01hksn98z890e489jcpmbbfq6a: {
		costInUsd: 25,
		credits: "25K",
		extra: "2.5K",
	},
	pri_01hksnabpd98m0k9ca5g4s453z: {
		costInUsd: 50,
		credits: "50K",
		extra: "5K",
	},
	pri_01hksnbcczzzqg93wn508yv9fa: {
		costInUsd: 100,
		credits: "100K",
		extra: "10K",
	},
}

const CREDIT_BUNDLE_IDS_LIVE = {
	pri_01hksyy2cgxfxr2m0e0fjt427h: {
		costInUsd: 1,
		credits: "1K",
		extra: null,
	},
	pri_01hksyypk9efcxk3087p30n4hn: {
		costInUsd: 5,
		credits: "5K",
		extra: null,
	},
	pri_01hksyz41hzf8hqcnxnnpsqtfx: {
		costInUsd: 10,
		credits: "10K",
		extra: "1K",
	},
	pri_01hksyzfxgbnbyke087sj2ns33: {
		costInUsd: 25,
		credits: "25K",
		extra: "2.5K",
	},
	pri_01hksyzw99vd6q3nxctdfmjyzp: {
		costInUsd: 50,
		credits: "50K",
		extra: "5K",
	},
	pri_01hksz07gn10wmypxvj9w8vk77: {
		costInUsd: 100,
		credits: "100K",
		extra: "10K",
	},
}

const CREDIT_BUNDLE_IDS =
	import.meta.env.MODE === "development"
		? CREDIT_BUNDLE_IDS_DEV
		: CREDIT_BUNDLE_IDS_LIVE

export const AICreditsPanel = ({ onTopUpComplete }) => {
	const { state, dispatch } = useContext(GlobalStateContext)
	const { aiState } = useContext(AIStateContext)
	const getCreditsAvailable = useGetCreditsAvailable()
	const { t } = useTranslation()

	const [loadingCredits, setLoadingCredits] = useState(false)
	const [firstLoad, setFirstLoad] = useState(true)

	const [credits, setCredits] = useState(0)
	const creditsRef = useRef(null)

	const [selectedPriceId, setSelectedPriceId] = useState(null)
	const [confirmPopoverOpen, setConfirmPopoverOpen] = useState(false)
	const [oneTimeChargeCreationError, setOneTimeChargeCreationError] =
		useState(null)

	const openCheckout = (creditsPriceId) => {
		window.Paddle.Checkout.open({
			settings: {
				allowLogout: false,
			},
			customer: state.paddleCustomerId
				? { id: state.paddleCustomerId }
				: { email: state.userEmail },
			items: [{ priceId: creditsPriceId, quantity: 1 }],
			customData: {
				firestoreUserId: state.userId,
			},
			customerAuthToken: state.customerAuthToken,
		})
	}

	const createOneTimeCharge = (creditsPriceId) => {
		if (!state.kopyPlusSubscription?.subscriptionId) return

		setConfirmPopoverOpen(false)
		setLoadingCredits(true)
		setFirstLoad(true)

		fetch("/api/paddle/createOneTimeCharge", {
			method: "POST",
			headers: { "Content-Type": "application/json" },
			body: JSON.stringify({
				priceId: creditsPriceId,
				subscriptionId: state.kopyPlusSubscription.subscriptionId,
			}),
		})
			.then((res) => res.json())
			.then(async (json) => {
				if (json?.success) {
					await new Promise((resolve) =>
						setTimeout(resolve, 1000 * 20),
					)
					if (onTopUpComplete) {
						onTopUpComplete()
					}
					await getCreditsAvailable({ updateState: true })
				} else {
					setOneTimeChargeCreationError(
						json?.error || t("error.unexpected_error"),
					)
					await new Promise((resolve) =>
						setTimeout(resolve, 1000 * 5),
					)
					setOneTimeChargeCreationError(null)
				}
			})
			.catch((err) => {
				console.error(err)
				setOneTimeChargeCreationError(
					`${t("error.unexpected_error")}. ${err.message}`,
				)
				setLoadingCredits(false)
				setFirstLoad(false)
			})
			.finally(() => {
				setLoadingCredits(false)
				setFirstLoad(false)
			})
	}

	useEffect(async () => {
		setLoadingCredits(true)
		await getCreditsAvailable({ updateState: true })
		setLoadingCredits(false)
		setFirstLoad(false)
	}, [])

	const activeSubscription = state.kopyPlusSubscription || null

	return (
		<div
			className="ai-credits-panel"
			style={{
				display: "flex",
				width: "fit-content",
				flexDirection: "column",
				gap: "1rem",
				padding: "1rem",
				background: "white",
				borderRadius: "1rem",
				boxShadow:
					"0 1px 3px 0 rgba(0,0,0,0.1), 0 1px 2px 0 rgba(0,0,0,0.06)",
				height: "fit-content",
				border: "1px solid #ccc",
			}}
		>
			<div
				style={{ display: "flex", gap: "0.3rem", alignItems: "center" }}
			>
				<svg
					xmlns="http://www.w3.org/2000/svg"
					width="1.8rem"
					height="1.8rem"
					viewBox="0 0 24 24"
				>
					<g fill="none" fillRule="evenodd">
						<path d="M24 0v24H0V0zM12.594 23.258l-.012.002l-.071.035l-.02.004l-.014-.004l-.071-.036c-.01-.003-.019 0-.024.006l-.004.01l-.017.428l.005.02l.01.013l.104.074l.015.004l.012-.004l.104-.074l.012-.016l.004-.017l-.017-.427c-.002-.01-.009-.017-.016-.018m.264-.113l-.014.002l-.184.093l-.01.01l-.003.011l.018.43l.005.012l.008.008l.201.092c.012.004.023 0 .029-.008l.004-.014l-.034-.614c-.003-.012-.01-.02-.02-.022m-.715.002a.023.023 0 0 0-.027.006l-.006.014l-.034.614c0 .012.007.02.017.024l.015-.002l.201-.093l.01-.008l.003-.011l.018-.43l-.003-.012l-.01-.01z"></path>
						<path
							fill="currentColor"
							d="M12 2C6.477 2 2 6.477 2 12s4.477 10 10 10s10-4.477 10-10S17.523 2 12 2m0 1a9 9 0 0 1-9 9a9 9 0 0 1 9 9a9 9 0 0 1 9-9a9 9 0 0 1-9-9"
						></path>
					</g>
				</svg>
				<div
					style={{
						fontSize: "2rem",
						fontWeight: "500",
						color: "#333",
					}}
				>
					{t("ai.credits")}
				</div>
			</div>

			<div style={{ textAlign: "center", marginBottom: "0.5rem" }}>
				<div
					style={{
						fontSize: "0.85rem",
						fontWeight: "500",
						color: "#777",
					}}
				>
					{t("ai.current_balance")}
				</div>

				{oneTimeChargeCreationError ? (
					<div
						style={{
							background: "#ff2f2f",
							padding: "0.75rem",
							borderRadius: "0.5rem",
						}}
					>
						<p
							style={{
								fontSize: "0.9rem",
								fontWeight: "600",
								color: "#fff",
								marginBottom: "1rem",
							}}
						>
							{oneTimeChargeCreationError}
						</p>
						<p
							style={{
								fontSize: "0.85rem",
								fontWeight: "500",
								color: "#fff",
								marginBottom: "1rem",
							}}
						>
							If purchasing keeps failing, please check that you
							have a valid payment method.
						</p>
						{activeSubscription?.updatePaymentMethodUrl && (
							<Button
								fullWidth
								onClick={() =>
									window.open(
										activeSubscription?.updatePaymentMethodUrl,
										"_blank",
									)
								}
							>
								Update Payment Method
							</Button>
						)}
					</div>
				) : (
					<div
						className="animate-character"
						style={{
							fontSize: "2.5rem",
							fontWeight: "bold",
							color: "#333",
						}}
					>
						{loadingCredits && firstLoad ? (
							<svg
								xmlns="http://www.w3.org/2000/svg"
								width="2rem"
								height="2rem"
								viewBox="0 0 24 24"
							>
								<circle
									cx="4"
									cy="12"
									r="3"
									fill="currentColor"
								>
									<animate
										id="svgSpinners3DotsBounce0"
										attributeName="cy"
										begin="0;svgSpinners3DotsBounce1.end+0.25s"
										calcMode="spline"
										dur="0.6s"
										keySplines=".33,.66,.66,1;.33,0,.66,.33"
										values="12;6;12"
									></animate>
								</circle>
								<circle
									cx="12"
									cy="12"
									r="3"
									fill="currentColor"
								>
									<animate
										attributeName="cy"
										begin="svgSpinners3DotsBounce0.begin+0.1s"
										calcMode="spline"
										dur="0.6s"
										keySplines=".33,.66,.66,1;.33,0,.66,.33"
										values="12;6;12"
									></animate>
								</circle>
								<circle
									cx="20"
									cy="12"
									r="3"
									fill="currentColor"
								>
									<animate
										id="svgSpinners3DotsBounce1"
										attributeName="cy"
										begin="svgSpinners3DotsBounce0.begin+0.2s"
										calcMode="spline"
										dur="0.6s"
										keySplines=".33,.66,.66,1;.33,0,.66,.33"
										values="12;6;12"
									></animate>
								</circle>
							</svg>
						) : (
							<CountUp
								start={0}
								end={aiState.creditsAvailable}
								duration={1.5}
								separator=","
							/>
						)}
					</div>
				)}
			</div>

			<div
				style={{
					display: "flex",
					flexWrap: "wrap",
					gap: "0.5rem",
					width: "18rem",
					marginBottom: "0.5rem",
					marginLeft: "auto",
					marginRight: "auto",
				}}
			>
				{Object.keys(CREDIT_BUNDLE_IDS).map((priceId) => {
					const bundle = CREDIT_BUNDLE_IDS[priceId]
					return (
						<div
							key={priceId}
							className="credits-price"
							style={{
								border:
									selectedPriceId === priceId
										? "1px solid #0469ff"
										: "1px solid #ddd",
								boxShadow:
									selectedPriceId === priceId
										? "0px 0px 0px 3px #e1effe"
										: "0 1px 3px 0 rgba(0,0,0,0.1), 0 1px 2px 0 rgba(0,0,0,0.06)",
							}}
							onClick={() => {
								setSelectedPriceId(priceId)
							}}
						>
							<div style={{ fontSize: "0.7rem" }}>
								{bundle.credits} credits
							</div>
							<div
								style={{
									fontSize: "1.3rem",
									fontWeight: "bold",
								}}
							>
								${bundle.costInUsd}
							</div>
							<div
								style={{
									fontSize: "0.6rem",
									fontWeight: "bold",
									color: "#00b467",
								}}
							>
								{bundle.extra
									? `+${bundle.extra} ${t("ai.extra_tokens_free")}`
									: "-"}
							</div>
						</div>
					)
				})}
			</div>

			<GlowingButtonContainer
				intensity={
					!selectedPriceId || loadingCredits ? "small" : "medium"
				}
			>
				<Popover
					zIndexOverride="550"
					active={confirmPopoverOpen}
					onClose={() => setConfirmPopoverOpen(false)}
					activator={
						!selectedPriceId || loadingCredits ? (
							<Button
								fullWidth
								disabled={!selectedPriceId || loadingCredits}
								onClick={() =>
									setConfirmPopoverOpen((prev) => !prev)
								}
							>
								{t("ai.buy_credits")}
							</Button>
						) : (
							<CoolButton
								fontSize="0.85rem"
								innerPadding="0.35rem"
								outerPadding="0.2rem"
								onClick={() => setConfirmPopoverOpen(true)}
							>
								<span style={{ fontWeight: "600" }}>
									{t("ai.buy_credits")}
								</span>
							</CoolButton>
						)
					}
				>
					<div style={{ padding: "0.5rem" }}>
						<p
							style={{
								fontSize: "0.9rem",
								fontWeight: "500",
								color: "#333",
								marginBottom: "0.5rem",
								padding: "0rem 0.5rem",
							}}
						>
							{t("ai.do_you_wish_to_purchase")}{" "}
							<b>
								{CREDIT_BUNDLE_IDS[selectedPriceId]?.credits ||
									0}
							</b>{" "}
							{t("ai.credits")}?
						</p>
						<CoolButton
							onClick={() => createOneTimeCharge(selectedPriceId)}
						>
							<span>
								{t("ai.confirm_credits_purchase")} (
								<span style={{ fontWeight: "700" }}>
									$
									{CREDIT_BUNDLE_IDS[selectedPriceId]
										?.costInUsd || 0}
								</span>
								)
							</span>
						</CoolButton>
					</div>
				</Popover>
			</GlowingButtonContainer>

			<style>{`
				.credits-price {
					border-radius: 1rem;
					background: #fff;
					padding: 0.3rem;
					display: flex;
					flex-direction: column;
					align-items: center;
					justify-content: space-between;
					cursor: pointer;
					flex: 1 0 30%;
					user-select: none;
				}

				.animate-character {
					background-image: linear-gradient(
						-300deg,
						#231557 0%,
						#44107a 5%,
						#ff1361 20%,
						#ffa100 50%,
						#ff1361 80%,
						#44107a 95%,
						#231557 100%
					);
					background-size: 200% auto;
					background-clip: text;
					-webkit-background-clip: text;
					text-fill-color: transparent;
					-webkit-text-fill-color: transparent;
					animation: textclip 3s linear infinite;
				}
				
				@keyframes textclip {
					to {
						background-position: 200% center;
					}
				}
			`}</style>
		</div>
	)
}
